import { useTranslation } from "react-i18next";
import { Area, CartesianGrid, ComposedChart, Line, ReferenceDot, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { GroupDailySpendSeriesResponse } from "../../api/organisationReporting";
import { formatDate, formatMoney, formatWholeMoney } from "../consumption/formatters";
import { ChartDefs, PulseDot } from "../charts/chartEffects";
import { useChartEffectIds } from "../charts/useChartEffectIds";
import { burnUpPoints } from "./groupPace";
import styles from "./GroupCard.module.css";

/**
 * A group's month as one line on one money scale: spend so far, the projection to month end, and the
 * allowance as a flat line, so the point where the group crosses its allowance is visible at a glance.
 * `compact` drops the axes and tooltip for the dense grid's small trend.
 */
export function GroupBurnUpChart({
  series,
  allowance,
  currency,
  period,
  compact = false,
}: {
  series: GroupDailySpendSeriesResponse | null;
  allowance: number | null;
  currency: string;
  period: { periodStart: string; periodEnd: string; generatedAt: string };
  compact?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const effects = useChartEffectIds();
  const height = compact ? 72 : 220;

  // The shell keeps its height before the series arrives, so the card does not jump when it does.
  if (!series || series.days.length === 0) {
    return <div className={styles.chart} style={{ height }} aria-hidden="true" />;
  }

  const points = burnUpPoints(series, period.periodStart, period.periodEnd, period.generatedAt);
  const highest = Math.max(allowance ?? 0, ...points.map((point) => Math.max(point.actual ?? 0, point.projected ?? 0)));
  const top = niceCeiling(highest * 1.05);
  // Today's total, pulsed on a month that is still running (one with a projection), as on My Usage.
  const today = !compact && series.projection ? [...points].reverse().find((point) => point.actual !== null) : undefined;

  return (
    <div className={styles.chart} style={{ height }} role="img" aria-label={t("myGroups.card.chartLabel")}>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={points} margin={compact ? { top: 6, right: 2, bottom: 2, left: 2 } : { top: 12, right: 12, bottom: 4, left: 4 }}>
          <ChartDefs ids={effects} gradients={[{ key: "actual", color: "var(--color-chart-pink)", top: 0.32, bottom: 0 }]} />
          {!compact && <CartesianGrid vertical={false} stroke="color-mix(in srgb, var(--color-border-default) 70%, transparent)" strokeDasharray="4 4" />}
          <XAxis
            dataKey="date"
            hide={compact}
            axisLine={false}
            tickLine={false}
            tickFormatter={(date: string) => formatDate(date)}
            tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }}
            minTickGap={32}
          />
          <YAxis
            hide={compact}
            axisLine={false}
            tickLine={false}
            domain={[0, top]}
            ticks={[0, top / 4, top / 2, (top * 3) / 4, top]}
            tickFormatter={(value: number) => formatWholeMoney(value, currency)}
            tick={{ fill: "var(--color-text-secondary)", fontSize: 12 }}
            width={64}
          />
          {!compact && (
            <Tooltip
              labelFormatter={(date) => formatDate(date as string)}
              formatter={(value, name) => [value === null ? "" : formatMoney(Number(value), currency), name]}
              cursor={{ stroke: "color-mix(in srgb, var(--color-chart-pink) 40%, transparent)" }}
            />
          )}
          {allowance !== null && (
            <ReferenceLine
              y={allowance}
              stroke="var(--color-text-secondary)"
              strokeDasharray="4 4"
              label={compact ? undefined : { value: t("myGroups.card.allowanceLine", { amount: formatWholeMoney(allowance, currency) }), position: "insideTopLeft", fill: "var(--color-text-secondary)", fontSize: 12 }}
            />
          )}
          <Area
            type="monotone"
            dataKey="actual"
            name={t("myGroups.card.spentSoFar")}
            stroke="var(--color-chart-pink)"
            strokeWidth={2.5}
            fill={`url(#${effects.gradient("actual")})`}
            filter={`url(#${effects.glow})`}
            connectNulls={false}
            isAnimationActive={false}
            dot={false}
          />
          <Line
            type="monotone"
            dataKey="projected"
            name={t("myGroups.card.projection")}
            stroke="var(--color-chart-pink)"
            strokeWidth={2.5}
            strokeDasharray="5 4"
            strokeLinecap="round"
            filter={`url(#${effects.glow})`}
            dot={false}
            isAnimationActive={false}
          />
          {today?.actual !== null && today?.actual !== undefined && (
            <ReferenceDot x={today.date} y={today.actual} fill="var(--color-chart-pink)" shape={PulseDot} />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Rounds a chart's top up to a tidy figure whose quarters are tidy too, so the axis ends, and steps, on round numbers. */
function niceCeiling(value: number): number {
  if (value <= 0) {
    return 1;
  }
  const magnitude = 10 ** Math.floor(Math.log10(value));
  const step = [1, 1.2, 1.6, 2, 2.4, 3, 4, 5, 6, 8, 10].find((candidate) => candidate * magnitude >= value) ?? 10;
  return step * magnitude;
}
